import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Button, Form, Spinner, Alert } from 'react-bootstrap';
import apiService from '../../services/api';
import RecordsTable from './RecordsTable';

/**
 * Shared shell for the Customer Data sections.
 *
 * Step 1: select a customer organization.
 * Step 2: pick a record type from the icon grid.
 */
const CustomerDataSection = ({ title, titleIcon, recordTypesLabel, recordTypes }) => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [loadingOrganizations, setLoadingOrganizations] = useState(true);
    const [error, setError] = useState('');
    const [activeRecordType, setActiveRecordType] = useState(null);

    useEffect(() => {
        loadOrganizations();
    }, []);

    const loadOrganizations = async () => {
        setLoadingOrganizations(true);
        setError('');

        try {
            const response = await apiService.getOrganizations(1, 1000, '');
            if (response.success) {
                const activeOrgs = (response.data || []).filter(
                    org => (org.subscription_status || '').toLowerCase() === 'active'
                );
                setOrganizations(activeOrgs);
            } else {
                setError(response.error || 'Failed to load organizations');
            }
        } catch (error) {
            console.error('Error loading organizations:', error);
            setError('Failed to load organizations');
        } finally {
            setLoadingOrganizations(false);
        }
    };

    const handleOrganizationChange = (e) => {
        const orgId = e.target.value;
        setActiveRecordType(null);
        if (orgId) {
            const org = organizations.find(o => o.id === parseInt(orgId));
            setSelectedOrganization(org);
        } else {
            setSelectedOrganization(null);
        }
    };

    const handleBack = () => setActiveRecordType(null);

    if (activeRecordType && selectedOrganization) {
        const recordType = recordTypes.find(rt => rt.key === activeRecordType);
        return (
            <Container fluid className="py-4">
                <Row className="mb-4">
                    <Col>
                        <h2><i className={`bi ${recordType.icon} me-2`}></i>{title} - {recordType.label}</h2>
                        <p className="text-muted mb-0">{selectedOrganization.name}</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="outline-secondary" onClick={handleBack}>
                            <i className="bi bi-arrow-left me-2"></i>Back
                        </Button>
                    </Col>
                </Row>

                <Card>
                    <Card.Header>
                        <i className={`bi ${recordType.icon} me-2`}></i>{recordType.label}
                    </Card.Header>
                    <Card.Body className={recordType.fetchRecords ? 'p-0' : ''}>
                        {recordType.fetchRecords ? (
                            <RecordsTable recordType={recordType} organization={selectedOrganization} />
                        ) : (
                            <Alert variant="info" className="mb-0">
                                <i className="bi bi-info-circle me-2"></i>
                                This section is under construction. {recordType.label} for {selectedOrganization.name} will
                                be available here.
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2><i className={`bi ${titleIcon} me-2`}></i>{title}</h2>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </Alert>
            )}

            <Card className="mb-4">
                <Card.Header>
                    <i className="bi bi-building me-2"></i>Select Organization
                </Card.Header>
                <Card.Body>
                    <Form.Group>
                        <Form.Label>Organization</Form.Label>
                        {loadingOrganizations ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading organizations...
                            </div>
                        ) : (
                            <Form.Select
                                value={selectedOrganization?.id || ''}
                                onChange={handleOrganizationChange}
                            >
                                <option value="">-- Select an Organization --</option>
                                {organizations.map(org => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </Form.Group>
                </Card.Body>
            </Card>

            {selectedOrganization && (
                <Card>
                    <Card.Header>
                        <i className="bi bi-grid me-2"></i>{recordTypesLabel} for {selectedOrganization.name}
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3">
                            {recordTypes.map(recordType => (
                                <Col key={recordType.key} xs={6} md={4} lg={3}>
                                    <Button
                                        variant="outline-primary"
                                        className="w-100 py-3 d-flex flex-column align-items-center"
                                        onClick={() => setActiveRecordType(recordType.key)}
                                    >
                                        <i className={`bi ${recordType.icon} fs-2 mb-2`}></i>
                                        <span>{recordType.label}</span>
                                    </Button>
                                </Col>
                            ))}
                        </Row>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default CustomerDataSection;
